import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Upload, Plus, Trash2, File, Image, Music } from "lucide-react";
import { QuestionModal } from "./QuestionModal";
import { subModuleService } from "../../services/subModuleService";
import { SubModule } from "types/subModule";
import { useToast } from "../ui/use-toast";
import { questionService } from "../../services/questionService";
import { moduleService } from "../../services/moduleService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Spinner } from "../ui/Spinner";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";

interface ClassroomItem {
  id: string;
  moduleId: number;
  name: string;
  type: string;
  content: string;
  videoUrl: string;
  sequenceNumber: number;
  pointsAwarded: number;
  startDate?: string;
  endDate?: string;
  questions: any[];
}

interface ClassroomModuleProps {
  moduleId?: string;
  onSave: (data: ClassroomItem[]) => void;
}

function getFileTypeFromPath(path: string): 'pdf' | 'image' | 'audio' | undefined {
  if (!path) return undefined;
  const ext = path.split('.').pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "gif"].includes(ext || "")) return "image";
  if (["mp3", "wav", "ogg"].includes(ext || "")) return "audio";
  if (ext === "pdf") return "pdf";
  return undefined;
}

// QuestionsView modal (read-only)
function QuestionsView({ isOpen, onClose, questions }: { isOpen: boolean; onClose: () => void; questions: any[] }) {
  const [previewImg, setPreviewImg] = React.useState<string | null>(null);
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Questions (View Only)</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto flex-1 pr-2">
            {questions.length === 0 && <div className="text-gray-500">No questions available.</div>}
            {questions.map((q, qIndex) => (
              <div key={q.id || qIndex} className="space-y-2 p-4 border rounded-lg">
                <div className="font-semibold">Q{qIndex + 1}: {q.question || q.text || q.questionText}</div>
                <div className="ml-4 space-y-1">
                  {Array.isArray(q.options) && q.options.map((opt: string, i: number) => (
                    <div key={i} className={q.correctAnswer === opt || q.correctAnswer === i ? "font-bold text-green-700" : ""}>
                      {String.fromCharCode(65 + i)}. {opt}
                      {q.correctAnswer === opt || q.correctAnswer === i ? " (Correct)" : ""}
                    </div>
                  ))}
                </div>
                {/* Preview for filePath */}
                {q.filePath && (
                  <div className="mt-2">
                    <div className="font-medium">Attachment Preview:</div>
                    <div className="flex items-center gap-2">
                      {getFileTypeFromPath(q.filePath) === 'image' ? (
                        <img
                          src={q.filePath}
                          alt="Preview"
                          className="h-10 w-10 object-cover rounded cursor-pointer"
                          onClick={() => setPreviewImg(q.filePath)}
                        />
                      ) : getFileTypeFromPath(q.filePath) === 'pdf' ? (
                        <a href={q.filePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">PDF</a>
                      ) : getFileTypeFromPath(q.filePath) === 'audio' ? (
                        <audio controls src={q.filePath} className="h-8" />
                      ) : null}
                      <span className="text-xs">{q.filePath.split("/").pop()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Image Preview Modal */}
      <Dialog open={!!previewImg} onOpenChange={() => setPreviewImg(null)}>
        <DialogContent className="flex items-center justify-center">
          {previewImg && (
            <img src={previewImg} alt="Full Preview" className="max-h-[80vh] max-w-full rounded shadow" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ClassroomModule({ moduleId, onSave }: ClassroomModuleProps) {
  const { toast } = useToast();
  const [classroomItems, setClassroomItems] = useState<ClassroomItem[]>([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalQuestions, setModalQuestions] = useState<any[]>([]);
  const [isQuestionsViewOpen, setIsQuestionsViewOpen] = useState(false);
  const [viewQuestions, setViewQuestions] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [classroomToDelete, setClassroomToDelete] = useState<string | null>(null);
  const [classroomPendingVideoRemove, setClassroomPendingVideoRemove] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const fetchClassrooms = async () => {
      if (moduleId) {
        try {
          setLoading(true);
          const response = await subModuleService.getSubModulesByModuleId(parseInt(moduleId), 'classroom');
          const classroomsArray = Array.isArray(response) ? response : [response];
          const classrooms = classroomsArray.map((classroom: SubModule) => ({
            id: classroom.id.toString(),
            moduleId: classroom.moduleId,
            name: classroom.name,
            type: classroom.type,
            content: classroom.content,
            videoUrl: classroom.videoUrl,
            sequenceNumber: classroom.sequenceNumber,
            pointsAwarded: classroom.pointsAwarded,
            startDate: classroom.startDate,
            endDate: classroom.endDate,
            questions: []
          }));
          setClassroomItems(classrooms);
        } catch (error) {
          console.error('Error fetching classrooms:', error);
          toast({
            title: "Error",
            description: "Failed to fetch classrooms",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    fetchClassrooms();
  }, [moduleId, toast]);

  const addNewClassroom = () => {
    const maxSequence = classroomItems.reduce((max, classroom) => classroom.sequenceNumber > max ? classroom.sequenceNumber : max, 20);
    const newClassroom: ClassroomItem = {
      id: "0",
      moduleId: parseInt(moduleId || "0"),
      name: "",
      type: "classroom",
      content: "",
      videoUrl: "",
      sequenceNumber: maxSequence + 1,
      pointsAwarded: 0,
      questions: []
    };
    setClassroomItems([...classroomItems, newClassroom]);
  };

  const removeClassroom = async (id: string) => {
    // If the classroom hasn't been saved yet (id is "0"), just remove from UI
    if (id === "0") {
      setClassroomItems(classroomItems.filter(item => item.id !== id));
      setIsDeleteDialogOpen(false);
      setClassroomToDelete(null);
      return;
    }

    try {
      setLoading(true);
      await subModuleService.deleteSubModule(parseInt(id));
      setClassroomItems(classroomItems.filter(item => item.id !== id));
      toast({
        title: "Success",
        description: "Classroom deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting classroom:', error);
      toast({
        title: "Error",
        description: "Failed to delete classroom",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsDeleteDialogOpen(false);
      setClassroomToDelete(null);
    }
  };

  const handleDeleteClick = (id: string) => {
    setClassroomToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const updateClassroomPoints = (id: string, points: string) => {
    setClassroomItems(classroomItems.map(item => 
      item.id === id ? { ...item, pointsAwarded: parseInt(points) || 0 } : item
    ));
  };

  const handleSaveClassroom = async (classroomId: string) => {
    const classroom = classroomItems.find(item => item.id === classroomId);
    if (!classroom || !moduleId) return;

    try {
      setLoading(true);
      const subModuleData: Partial<SubModule> = {
        id: isNaN(Number(classroom.id)) ? 0 : Number(classroom.id),
        moduleId: parseInt(moduleId),
        name: classroom.name,
        type: 'classroom',
        content: classroom.content,
        videoUrl: classroom.videoUrl,
        sequenceNumber: classroom.sequenceNumber,
        pointsAwarded: classroom.pointsAwarded,
        startDate: classroom.startDate,
        endDate: classroom.endDate
      };

      const savedClassroom = await subModuleService.saveSubModule(subModuleData);
      setClassroomItems(classroomItems.map(item =>
        item.id === classroomId
          ? { ...item, id: savedClassroom.id.toString() }
          : item
      ));

      toast({
        title: "Success",
        description: "Classroom saved successfully",
      });
    } catch (error) {
      console.error('Error saving classroom:', error);
      toast({
        title: "Error",
        description: "Failed to save classroom",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSave = (question: any) => {
    if (selectedClassroomId) {
      setClassroomItems(classroomItems.map(item =>
        item.id === selectedClassroomId
          ? { ...item, questions: [...item.questions, question] }
          : item
      ));
    }
  };

  const handleVideoUpload = async (classroomId: string, file: File) => {
    try {
      setIsUploading(prev => ({ ...prev, [classroomId]: true }));
      setUploadProgress(prev => ({ ...prev, [classroomId]: 0 }));
      
      const data = await moduleService.uploadFile(file, (progress) => {
        setUploadProgress(prev => ({ ...prev, [classroomId]: progress }));
      });
      
      const videoUrl = data.url;
      setClassroomItems(classroomItems.map(item =>
        item.id === classroomId ? { ...item, videoUrl } : item
      ));
      
      toast({
        title: "Success",
        description: "Video uploaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload video",
        variant: "destructive",
      });
    } finally {
      setIsUploading(prev => ({ ...prev, [classroomId]: false }));
      setUploadProgress(prev => ({ ...prev, [classroomId]: 0 }));
    }
  };

  if (loading) {
    return <Spinner label="Loading..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Classroom Training Configuration</h3>
        <Button onClick={addNewClassroom}>
          <Plus className="mr-2 h-4 w-4" />
          Add New Training
        </Button>
      </div>

      {classroomItems.map((classroom, index) => (
        <Card key={classroom.id}>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Classroom Training {index + 1}</CardTitle>
              <div className="flex items-center gap-2 hidden">
                <Label htmlFor={`sequence-${classroom.id}`} className="shrink-0">Sequence No.</Label>
                <Input
                  id={`sequence-${classroom.id}`}
                  type="number"
                  value={classroom.sequenceNumber}
                  onChange={(e) => {
                    const newSequence = parseInt(e.target.value, 10);
                    setClassroomItems(classroomItems.map(item =>
                      item.id === classroom.id ? { ...item, sequenceNumber: isNaN(newSequence) ? 0 : newSequence } : item
                    ));
                  }}
                  className="w-24"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`name-${classroom.id}`}>Name</Label>
                <Input
                  id={`name-${classroom.id}`}
                  value={classroom.name}
                  onChange={(e) => setClassroomItems(classroomItems.map(item =>
                    item.id === classroom.id ? { ...item, name: e.target.value } : item
                  ))}
                  placeholder="Enter classroom name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`content-${classroom.id}`}>Description</Label>
                <Input
                  id={`content-${classroom.id}`}
                  value={classroom.content}
                  onChange={(e) => setClassroomItems(classroomItems.map(item =>
                    item.id === classroom.id ? { ...item, content: e.target.value } : item
                  ))}
                  placeholder="Enter classroom description"
                />
              </div>
              </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`points-${classroom.id}`}>Points</Label>
                <Input 
                  id={`points-${classroom.id}`} 
                  type="number" 
                  placeholder="Enter points"
                  value={classroom.pointsAwarded.toString()}
                  onChange={(e) => updateClassroomPoints(classroom.id, e.target.value)}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`start-date-${classroom.id}`}>Start Date</Label>
                <DatePicker
                  id={`start-date-${classroom.id}`}
                  selected={classroom.startDate ? parseISO(classroom.startDate) : null}
                  onChange={(date: Date | null) =>
                    setClassroomItems(classroomItems.map(item =>
                      item.id === classroom.id ? { ...item, startDate: date ? format(date, "yyyy-MM-dd") : "" } : item
                    ))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Select start date"
                  disabled={loading}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label htmlFor={`end-date-${classroom.id}`}>End Date</Label>
                <DatePicker
                  id={`end-date-${classroom.id}`}
                  selected={classroom.endDate ? parseISO(classroom.endDate) : null}
                  onChange={(date: Date | null) =>
                    setClassroomItems(classroomItems.map(item =>
                      item.id === classroom.id ? { ...item, endDate: date ? format(date, "yyyy-MM-dd") : "" } : item
                    ))
                  }
                  dateFormat="yyyy-MM-dd"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Select end date"
                  minDate={classroom.startDate ? parseISO(classroom.startDate) : undefined}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`video-url-${classroom.id}`}>Video URL</Label>
              <Input
                id={`video-url-${classroom.id}`}
                type="url"
                placeholder="Paste video URL here (e.g. YouTube, Vimeo, etc.)"
                value={classroom.videoUrl && classroom.videoUrl.startsWith('http') ? classroom.videoUrl : ''}
                onChange={e => {
                  const url = e.target.value;
                  setClassroomItems(classroomItems.map(item =>
                    item.id === classroom.id ? { ...item, videoUrl: url } : item
                  ));
                }}
                disabled={!!(classroom.videoUrl && !classroom.videoUrl.startsWith('http') && classroom.videoUrl !== '')}
              />
              {/* Hidden field for uploaded video URLs */}
              <Input
                type="hidden"
                value={classroom.videoUrl && !classroom.videoUrl.startsWith('http') ? classroom.videoUrl : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>Upload Video</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6"
                onDragOver={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (file) handleVideoUpload(classroom.id, file);
                }}
              >
                <div className="flex flex-row items-center gap-6">
                  <div className="flex flex-col items-center justify-center flex-1">
                    <Upload className="h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2 text-center">Drop your video here or click to browse</p>
                    <input
                      type="file"
                      accept="video/*"
                      style={{ display: "none" }}
                      id={`video-upload-${classroom.id}`}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleVideoUpload(classroom.id, file);
                      }}
                      disabled={!!(classroom.videoUrl && classroom.videoUrl.startsWith('http')) || isUploading[classroom.id]}
                    />
                    <Button
                      onClick={() => document.getElementById(`video-upload-${classroom.id}`)?.click()}
                      disabled={!!(classroom.videoUrl && classroom.videoUrl.startsWith('http')) || isUploading[classroom.id]}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {isUploading[classroom.id] ? 'Uploading...' : 'Upload Video'}
                    </Button>
                    
                    {/* Progress Bar */}
                    {isUploading[classroom.id] && (
                      <div className="w-full mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress[classroom.id] || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[classroom.id] || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {classroom.videoUrl && !classroom.videoUrl.startsWith('http') && (
                    <div className="flex-1 flex items-center justify-center">
                      <video src={classroom.videoUrl} controls width="220" className="rounded shadow" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 p-2"
                        onClick={() => setClassroomPendingVideoRemove(classroom.id)}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  )}
                  {classroom.videoUrl && classroom.videoUrl.startsWith('http') && (
                    <div className="flex-1 flex items-center justify-center gap-4">
                      {/\.(mp4|webm|ogg)$/i.test(classroom.videoUrl)
                        ? <video src={classroom.videoUrl} controls width="220" className="rounded shadow" />
                        : <a href={classroom.videoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Video URL</a>
                      }
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 p-2"
                        onClick={() => setClassroomPendingVideoRemove(classroom.id)}
                      >
                        <Trash2 className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-end gap-2">
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={() => handleSaveClassroom(classroom.id)}
                  disabled={loading}
                >
                  Save Classroom
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={async () => {
                    if (classroom.id && classroom.id !== '0' && !isNaN(Number(classroom.id))) {
                      setSelectedClassroomId(classroom.id);
                      setLoading(true);
                      try {
                        const questions = await questionService.getQuestions(Number(classroom.id));
                        setModalQuestions(questions);
                        setIsQuestionModalOpen(true);
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: "Failed to fetch questions",
                          variant: "destructive",
                        });
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      toast({
                        title: "Save the classroom first",
                        description: "Please save the classroom before adding questions.",
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={loading}
                >
                  Add Questions
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const questions = await questionService.getQuestions(Number(classroom.id));
                      setViewQuestions(questions);
                      setIsQuestionsViewOpen(true);
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Failed to fetch questions",
                        variant: "destructive",
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  View Questions
                </Button>
              </div>
              {classroom.questions.length > 0 && (
                <div className="mt-2 space-y-2">
                  {classroom.questions.map((question, qIndex) => (
                    <div key={qIndex} className="p-2 bg-gray-50 rounded">
                      {question.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* QuestionsView Modal */}
      <QuestionsView
        isOpen={isQuestionsViewOpen}
        onClose={() => setIsQuestionsViewOpen(false)}
        questions={viewQuestions}
      />

      <QuestionModal 
        reelId={selectedClassroomId || ""}
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setSelectedClassroomId(null);
        }}
        onSave={handleQuestionSave}
        initialQuestions={Array.isArray(modalQuestions) ? modalQuestions : []}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Classroom</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this classroom? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setClassroomToDelete(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => classroomToDelete && removeClassroom(classroomToDelete)}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Remove Confirmation Dialog */}
      <Dialog open={!!classroomPendingVideoRemove} onOpenChange={() => setClassroomPendingVideoRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Video</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this video? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClassroomPendingVideoRemove(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => {
              setClassroomItems(classroomItems.map(item =>
                item.id === classroomPendingVideoRemove ? { ...item, videoUrl: '' } : item
              ));
              setClassroomPendingVideoRemove(null);
            }}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 